import type { IDynamicFilterControl } from '@gp/components/DynamicFilter/components/FilterControl/types';
import type { IDynamicFilterField } from '@gp/components/DynamicFilter/types';
import FilterCommon from '@gp/components/FilterCommon';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';
import { COMMON_SELECT_MULTIPLE_CONTROL_CFG } from '@gp/constants/select';
import type { IRecord } from '@gp/types/global';
import type { IBaseProps } from '@home/types/common';
import classnames from 'classnames';
import { cloneDeep, isEqual, merge } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';

import './index.scss';

interface IFilterProps extends IBaseProps {
  loading?: boolean;
  disabled?: boolean;
  data?: IPipelineProjectListFilterConditionsVO;
  value?: IPipelineProjectListFilterVO;
  onChange?: (payload: IPipelineProjectListFilterVO) => void;
}

const DEFAULT_CONTROLS: IRecord<IDynamicFilterControl> = {
  project_id: {
    options: [],
  },
  platform: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  milestone: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    searchable: false,
    listWidth: 340,
    options: [],
  },
  next_milestone: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    searchable: false,
    listWidth: 340,
    options: [],
  },
  publisher: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    matchButtonWidth: false,
    searchable: false,
    listWidth: 260,
    options: [],
  },
  // status: {
  //   ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
  //   searchable: false,
  //   options: [],
  // },
  stage: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
  form: {
    ...COMMON_SELECT_MULTIPLE_CONTROL_CFG,
    searchable: false,
    options: [],
  },
};

const getBoxClassName = (key: string, isEmptyOnSearch?: boolean) => {
  switch (key) {
    case KEYS_MAPPING.STUDIO:
    case KEYS_MAPPING.BUSINESS_OWNER:
      return classnames(
        'globalpedia-custom-scrollbar globalpedia-modal-font14 dbg-common-select-multiple-overlay',
        {
          'globalpedia-filter-search-list-empty': !!isEmptyOnSearch,
        },
      );
    case KEYS_MAPPING.GROUP:
      return 'globalpedia-modal-font14 dbg-common-select-multiple-overlay';
    case KEYS_MAPPING.PUBLISHER:
      return 'globalpedia-custom-scrollbar globalpedia-modal-font14 globalpedia-modal-item-text-ellipsis';
    default:
      return 'globalpedia-custom-scrollbar globalpedia-modal-font14 dbg-common-select-multiple-overlay';
  }
};

const Filter: React.FC<IFilterProps> = (props) => {
  const {
    style,
    className,
    loading = true,
    disabled = false,
    data,
    value: valueProps,
    onChange: onChangeProps,
    allFilterOptionsValues,
    onReset,
    setIsOverFiler,
    scrollBody,
    showRightBtn,
    currentPage,
    formatSearchResults,
    updateDeps,
    searchVal,
    searchList,
    handleSearch,
    headerTitle,
    selectedValue,
    setSelectedValue,
    setSearchList,
    setSearchVal,
  } = props;

  const controls = useMemo<IRecord<IDynamicFilterControl>>(() => {
    if (loading) return DEFAULT_CONTROLS;
    const {
      platform: platformOptions,
      milestone: milestoneOptions,
      next_milestone: nextMilestoneOptions,
      publisher: publisherOptions,
      // status: statusOptions,
      stage: stageOptions,
      form: groupOptions,
      project_id: cascadeProjectOptions,
    } = data || {};
    const controlsTmp = cloneDeep(DEFAULT_CONTROLS);

    return merge(controlsTmp, {
      project_id: {
        options: cascadeProjectOptions,
        allValues: allFilterOptionsValues?.project_id || [],
        btnTitle: 'P-team/BO/Studio/Project',
      },
      platform: {
        boxClassName: getBoxClassName(KEYS_MAPPING.PLATFORM),
        disabled,
        options: platformOptions,
      },
      milestone: {
        boxClassName: getBoxClassName(KEYS_MAPPING.MILESTONE),
        disabled,
        options: milestoneOptions,
      },
      next_milestone: {
        boxClassName: getBoxClassName(KEYS_MAPPING.NEXT_MILESTONE),
        disabled,
        options: nextMilestoneOptions,
      },
      publisher: {
        boxClassName: getBoxClassName(KEYS_MAPPING.PUBLISHER),
        disabled,
        options: publisherOptions,
      },
      // status: {
      //   boxClassName: getBoxClassName(KEYS_MAPPING.STATUS),
      //   disabled,
      //   options: statusOptions,
      // },
      stage: {
        boxClassName: getBoxClassName(KEYS_MAPPING.STAGE),
        disabled,
        options: stageOptions,
      },
      form: {
        boxClassName: getBoxClassName(KEYS_MAPPING.GROUP),
        disabled,
        options: groupOptions,
      },
    });
  }, [loading, disabled, data]);

  const [value, setValue] = useState<IPipelineProjectListFilterVO>({
    project_id: [],
    platform: [],
    milestone: [],
    next_milestone: [],
    publisher: [],
    status: [],
    stage: [],
    form: [],
  });

  const fields = useMemo<IDynamicFilterField[]>(() => {
    const {
      project_id: projectOptions,
      status: statusOptions,
      form: groupOptions,
    } = data || {};
    const projectDisplay = loading
      ? false
      : Array.isArray(projectOptions) && projectOptions.length > 0;
    const statusDisplay = loading
      ? false
      : Array.isArray(statusOptions) && statusOptions.length > 0;
    return [
      {
        key: KEYS_MAPPING.CASCADE_PROJECT,
        type: 'cascadeProject',
        display: projectDisplay,
        label: NAMES_MAPPING.CASCADE_PROJECT,
      },
      {
        key: KEYS_MAPPING.PLATFORM,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.PLATFORM,
      },
      {
        className: 'dbg-pipeline-projectlist-filter-form-item-milestone',
        key: KEYS_MAPPING.MILESTONE,
        type: 'selectMultiple',
        display: true,
        label: 'Current Milestone',
        saveKey: 'cur_milestone',
      },
      {
        className: 'dbg-pipeline-projectlist-filter-form-item-milestone',
        key: KEYS_MAPPING.NEXT_MILESTONE,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.NEXT_MILESTONE,
      },
      {
        key: KEYS_MAPPING.PUBLISHER,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.PUBLISHER,
      },
      // {
      //   key: KEYS_MAPPING.STATUS,
      //   type: 'selectMultiple',
      //   display: statusDisplay,
      //   label: NAMES_MAPPING.STATUS,
      // },
      {
        key: KEYS_MAPPING.STAGE,
        type: 'selectMultiple',
        display: true,
        label: NAMES_MAPPING.STAGE,
      },
    ];
  }, [loading, data]);

  const handleChange = (payload: IRecord<IPipelineProjectListFilterVO>) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
  };

  const handleConfirm = () => {
    onChangeProps?.(value);
  };

  useEffect(() => {
    if (!isEqual(value, valueProps)) {
      setValue({ ...value, ...valueProps });
    }
  }, [valueProps]);

  return (
    <FilterCommon
      style={style}
      className={className}
      controls={controls}
      value={value}
      onChange={handleChange}
      isNeedDiff={true}
      fields={fields}
      confirm={handleConfirm}
      onReset={onReset}
      loading={loading}
      setIsOverFiler={setIsOverFiler}
      scrollBody={scrollBody}
      filterSearchHeight={110}
      filterHeight={86}
      headerTitle={headerTitle}
      showRightBtn={showRightBtn}
      currentPage={currentPage}
      formatSearchResults={formatSearchResults}
      updateDeps={updateDeps}
      searchVal={searchVal}
      searchList={searchList}
      handleSearch={handleSearch}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      setSearchList={setSearchList}
      setSearchVal={setSearchVal}
    />
  );
};

export default Filter;
